import React from "react";
import { FileText, Clock, Users, Camera, CreditCard, AlertCircle } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { businessInfo } from "../data/mock";
import "../styles/Policies.css";

const Policies = () => {
  const policies = [
    {
      icon: <FileText size={32} />,
      title: "Age Requirements",
      content: "Facial treatments: 16 years and over. IV vitamin drips: 18 years and over, or 16+ with parental consent."
    },
    {
      icon: <AlertCircle size={32} />,
      title: "Consultations",
      content: "A consultation is required prior to treatment to ensure suitability and the correct treatment choice. This may be completed online or face-to-face, depending on the service."
    },
    {
      icon: <FileText size={32} />,
      title: "Medical and Consent Forms",
      content: "All clients must complete medical history and consent forms prior to treatment. Forms will be provided via email and/or in clinic and must be completed before your appointment."
    },
    {
      icon: <Users size={32} />,
      title: "Appointments & Guests",
      content: "We kindly ask that clients attend appointments alone. No additional guests or pets are permitted unless: You are both booked in for treatments, or prior arrangements have been agreed. Please DM us in advance if you require a plus-one."
    },
    {
      icon: <CreditCard size={32} />,
      title: "Deposits & Payment",
      content: "A 20% deposit is required for all treatments at booking. The deposit is non-refundable unless cancelled with at least 48 hours notice. Remaining balance is due on arrival."
    },
    {
      icon: <Clock size={32} />,
      title: "Cancellations & Rescheduling",
      content: "Please provide at least 48 hours notice for cancellations or rescheduling. Cancellations made with 48+ hours notice will receive a full deposit refund. Cancellations made less than 48 hours before appointment will forfeit the deposit. No-shows will forfeit the full deposit. We understand life happens - contact us as soon as possible and we'll work with you."
    },
    {
      icon: <AlertCircle size={32} />,
      title: "Late Arrivals",
      content: "Please arrive 10 minutes early for your appointment. If running late, contact us immediately. Excessive lateness (15+ minutes) may result in rescheduling if another client is booked after you. Your appointment time may be reduced to accommodate the schedule."
    },
    {
      icon: <Camera size={32} />,
      title: "Before & After Photos",
      content: "Photos will be taken for treatment records and insurance purposes. You can opt-in on your consent form if you're happy for images to be used on social media. We respect your privacy and will never share photos without your explicit consent."
    }
  ];

  const aftercareInstructions = [
    {
      category: "IV Infusions & IM Injections",
      instructions: [
        "Drink plenty of water after your treatment",
        "Avoid strenuous exercise for 24 hours",
        "Some bruising or tenderness at injection site is normal",
        "Contact us if you experience unusual symptoms",
        "Results may take 3-5 days to become noticeable"
      ]
    },
    {
      category: "Botox & Anti-Wrinkle Treatments",
      instructions: [
        "Do not touch or massage the treated area for 24 hours",
        "Avoid lying down for 4 hours after treatment",
        "No strenuous exercise for 24 hours",
        "Avoid alcohol and excessive heat (saunas, hot tubs) for 24 hours",
        "Results typically appear within 3-7 days",
        "Follow-up appointment in 2 weeks for assessment"
      ]
    },
    {
      category: "Fat Dissolving (Lemon Bottle)",
      instructions: [
        "Mild swelling and redness are normal and will subside",
        "Drink plenty of water to help flush out fat cells",
        "Light exercise is encouraged after 24 hours",
        "Avoid alcohol for 24 hours",
        "Results develop over 2-3 weeks as fat cells are metabolized",
        "Multiple sessions may be needed for optimal results"
      ]
    },
    {
      category: "General Skincare Aftercare",
      instructions: [
        "Keep the treated area clean and moisturized",
        "Avoid direct sun exposure and use SPF 30+",
        "No makeup for 24 hours after facial treatments",
        "Avoid harsh skincare products for 48 hours",
        "Stay hydrated and maintain a healthy skincare routine"
      ]
    }
  ];

  return (
    <>
      <Navigation />
      <div className="policies-page">
      <section className="policies-hero">
        <div className="container">
          <h1 className="hero-large">POLICIES & AFTERCARE</h1>
          <p className="body-large">Your safety and satisfaction are our top priorities</p>
        </div>
      </section>

      <section className="policies-content section-padding">
        <div className="container">
          <div className="policies-intro">
            <h2 className="heading-2">General Policies</h2>
            <p className="body-large">
              Please review our policies before your appointment. If you have any questions, 
              don't hesitate to contact us.
            </p>
          </div>

          <div className="policies-grid">
            {policies.map((policy, index) => (
              <div key={index} className="policy-card">
                <div className="policy-icon">{policy.icon}</div>
                <h3 className="heading-3">{policy.title}</h3>
                <p className="body-regular">{policy.content}</p>
              </div>
            ))}
          </div>

          <div className="important-notes">
            <h3 className="heading-2">Important Information</h3>
            <ul className="body-regular">
              <li><strong>Clothing:</strong> Wear comfortable clothing for your treatment</li>
              <li><strong>Preparation:</strong> Please eat and drink before your appointment</li>
              <li><strong>Makeup:</strong> Come with a clean face or be prepared to remove makeup</li>
              <li><strong>Allergies:</strong> Inform us of any severe allergies at booking (nuts, animals, etc.)</li>
              <li><strong>Parking:</strong> Available outside and to the rear of the clinic at {businessInfo.address}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="aftercare-section section-padding">
        <div className="container">
          <div className="aftercare-intro">
            <h2 className="heading-2">Aftercare Instructions</h2>
            <p className="body-large">
              Follow these guidelines to ensure optimal results and recovery from your treatment.
            </p>
          </div>

          <div className="aftercare-list">
            {aftercareInstructions.map((item, index) => (
              <div key={index} className="aftercare-item">
                <h3 className="heading-3">{item.category}</h3>
                <ul className="body-regular">
                  {item.instructions.map((instruction, idx) => (
                    <li key={idx}>{instruction}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="aftercare-cta">
            <h3 className="heading-2">Questions or Concerns?</h3>
            <p className="body-large">
              If you experience any unusual symptoms or have questions about your aftercare, 
              please contact us immediately.
            </p>
            <div className="cta-buttons">
              <a href={`tel:${businessInfo.phone}`} className="btn-primary">
                CALL US: {businessInfo.phone}
              </a>
              <a href={`mailto:${businessInfo.email}`} className="btn-secondary">
                EMAIL US
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default Policies;
